import { useEffect, useState } from 'react';
import { 
  Coffee, Utensils, Dumbbell, Heart, XCircle, 
  ArrowLeft, CheckCircle2, Loader2, Trash2, ChevronDown, ChevronUp, Check, Target
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';

// Tipos
type DesafioDisponivel = {
  id: string; // UUID
  titulo: string;
  regra: string;
  objetivo: string;
  icon: string | null;
  imagem_url?: string | null; // Para compatibilidade
};

type UserDesafio = {
  id: string; // UUID
  user_id: string;
  desafio_id: string; // UUID
  created_at: string;
  updated_at: string;
  desafio?: DesafioDisponivel;
};

type DesafioHistorico = {
  id: string; // UUID no banco
  user_id: string;
  desafio_id: string; // UUID
  data: string;
  anotacao: string; // Nome correto da coluna no banco
  progresso?: string; // Mantido para compatibilidade
};

// Constantes visuais
const BORDER_COLOR = 'rgba(255, 255, 255, 0.1)';
const BG_DARK = '#0D0D0D';
const CARD_BG = '#141414';

// Função para determinar ícone baseado no título ou icon
const getIcon = (titulo: string, icon?: string | null, imagem_url?: string | null) => {
  const iconSource = icon || imagem_url;
  
  if (iconSource) {
    const iconLower = iconSource.toLowerCase();
    if (iconLower.includes('coffee') || iconLower.includes('café')) return Coffee;
    if (iconLower.includes('heart') || iconLower.includes('flame') || iconLower.includes('cardio')) return Heart;
    if (iconLower.includes('utensils') || iconLower.includes('plate') || iconLower.includes('refeição')) return Utensils;
    if (iconLower.includes('dumbbell') || iconLower.includes('weight') || iconLower.includes('carga')) return Dumbbell;
    if (iconLower.includes('x') || iconLower.includes('circle') || iconLower.includes('zero')) return XCircle;
  }
  
  const tituloLower = titulo.toLowerCase();
  if (tituloLower.includes('café') || tituloLower.includes('açúcar')) {
    return Coffee;
  } else if (tituloLower.includes('cardio') || tituloLower.includes('pós-treino')) {
    return Heart;
  } else if (tituloLower.includes('refeição') || tituloLower.includes('dieta')) {
    return Utensils;
  } else if (tituloLower.includes('carga') || tituloLower.includes('progressão')) {
    return Dumbbell;
  } else if (tituloLower.includes('ultraprocessado') || tituloLower.includes('zero')) {
    return XCircle;
  }
  return Dumbbell;
};

export default function Challenges() {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState<'lista' | 'detalhes' | 'acompanhamento'>('lista');
  const [loading, setLoading] = useState(true);
  const [desafiosDisponiveis, setDesafiosDisponiveis] = useState<DesafioDisponivel[]>([]);
  const [desafiosAtivos, setDesafiosAtivos] = useState<UserDesafio[]>([]);
  const [selectedDesafioId, setSelectedDesafioId] = useState<string | null>(null);
  const [selectedUserDesafioId, setSelectedUserDesafioId] = useState<string | null>(null);
  const [currentDesafio, setCurrentDesafio] = useState<DesafioDisponivel | null>(null);
  const [currentUserDesafio, setCurrentUserDesafio] = useState<UserDesafio | null>(null);
  const [historico, setHistorico] = useState<DesafioHistorico[]>([]);
  const [progressoTexto, setProgressoTexto] = useState('');
  const [objetivoExpandido, setObjetivoExpandido] = useState(false);
  const [addingDesafio, setAddingDesafio] = useState<Record<string, boolean>>({});
  const [removing, setRemoving] = useState(false);
  const [savingProgresso, setSavingProgresso] = useState(false);

  // Carregar desafios disponíveis
  const loadDesafiosDisponiveis = async () => {
    try {
      const { data, error } = await supabase
        .from('desafios_disponiveis')
        .select('*')
        .order('titulo');

      if (error) {
        console.error('Erro ao carregar desafios disponíveis:', error);
        setDesafiosDisponiveis([]);
      } else {
        setDesafiosDisponiveis(data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar desafios disponíveis:', error);
    }
  };

  // Carregar todos os desafios ativos do usuário
  const loadDesafiosAtivos = async () => {
    if (!user) {
      setDesafiosAtivos([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_desafio')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar desafios ativos:', error);
        setDesafiosAtivos([]);
        return;
      }

      if (data && data.length > 0) {
        // Carregar dados completos de cada desafio
        const desafiosCompletos = await Promise.all(
          data.map(async (userDesafio: UserDesafio) => {
            const { data: desafioData } = await supabase
              .from('desafios_disponiveis')
              .select('*')
              .eq('id', userDesafio.desafio_id)
              .single();

            return {
              ...userDesafio,
              desafio: desafioData,
            };
          })
        );

        setDesafiosAtivos(desafiosCompletos);
      } else {
        setDesafiosAtivos([]);
      }
    } catch (error) {
      console.error('Erro ao carregar desafios ativos:', error);
      setDesafiosAtivos([]);
    }
  };

  // Carregar dados de um user_desafio específico pelo id
  const loadUserDesafioById = async (userDesafioId: string) => {
    if (!user) {
      setCurrentUserDesafio(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_desafio')
        .select('*')
        .eq('id', userDesafioId)
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Erro ao carregar user_desafio:', error);
        setCurrentUserDesafio(null);
        return;
      }

      if (data) {
        // Carregar dados completos do desafio
        const { data: desafioData } = await supabase
          .from('desafios_disponiveis')
          .select('*')
          .eq('id', data.desafio_id)
          .single();

        setCurrentUserDesafio({
          ...data,
          desafio: desafioData,
        });
        setCurrentDesafio(desafioData);
      } else {
        setCurrentUserDesafio(null);
        setCurrentDesafio(null);
      }
    } catch (error) {
      console.error('Erro ao carregar user_desafio:', error);
      setCurrentUserDesafio(null);
      setCurrentDesafio(null);
    }
  };

  // Carregar histórico do desafio baseado no userDesafioId
  const loadHistorico = async (userDesafioId: string) => {
    if (!user || !userDesafioId) {
      setHistorico([]);
      return;
    }

    try {
      // Buscar o desafio_id do registro user_desafio
      const { data: userDesafioData } = await supabase
        .from('user_desafio')
        .select('desafio_id')
        .eq('id', userDesafioId)
        .eq('user_id', user.id)
        .single();

      if (!userDesafioData) {
        setHistorico([]);
        return;
      }

      const { data, error } = await supabase
        .from('desafio_historico')
        .select('id, user_id, desafio_id, data, anotacao')
        .eq('user_id', user.id)
        .eq('desafio_id', userDesafioData.desafio_id)
        .order('data', { ascending: false });

      if (error) {
        console.error('Erro ao carregar histórico:', error);
        setHistorico([]);
      } else {
        setHistorico(data || []);
        
        // Carregar progresso do dia atual se existir (apenas se não for "Dia concluído")
        const hoje = new Date().toISOString().split('T')[0];
        const hojeHistorico = data?.find((h: DesafioHistorico) => {
          const texto = h.anotacao || h.progresso || '';
          const isConcluido = texto.trim() === '✅ Dia concluído' || texto.trim().startsWith('✅');
          return h.data === hoje && !isConcluido;
        });
        if (hojeHistorico) {
          setProgressoTexto(hojeHistorico.anotacao || hojeHistorico.progresso || '');
        } else {
          setProgressoTexto('');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setHistorico([]);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadDesafiosDisponiveis(),
        loadDesafiosAtivos(),
      ]);
      setLoading(false);
    };

    if (user) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Recarregar desafios ativos quando voltar para lista
  useEffect(() => {
    if (currentView === 'lista' && user) {
      loadDesafiosAtivos();
    }
  }, [currentView, user]);

  // Carregar dados quando entrar na aba de detalhes
  useEffect(() => {
    if (currentView === 'detalhes' && selectedDesafioId) {
      const desafio = desafiosDisponiveis.find(d => d.id === selectedDesafioId);
      setCurrentDesafio(desafio || null);
    }
  }, [currentView, selectedDesafioId, desafiosDisponiveis]);

  // Carregar dados quando entrar na aba de acompanhamento
  useEffect(() => {
    if (currentView === 'acompanhamento' && selectedUserDesafioId && user) {
      loadUserDesafioById(selectedUserDesafioId);
      loadHistorico(selectedUserDesafioId);
    }
  }, [currentView, selectedUserDesafioId, user]);

  // Verificar se um desafio já está ativo para o usuário
  const isDesafioAtivo = (desafioId: string): UserDesafio | null => {
    return desafiosAtivos.find(ud => ud.desafio_id === desafioId) || null;
  };

  // Começar desafio - criar registro e navegar para acompanhamento
  const handleComecarDesafio = async (desafioId: string) => {
    if (!user) {
      alert('Você precisa estar logado para começar um desafio.');
      return;
    }

    // Validar que desafioId é um UUID válido
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(desafioId)) {
      console.error('desafioId não é um UUID válido:', desafioId);
      alert('Erro: ID do desafio inválido. Por favor, recarregue a página.');
      return;
    }

    setAddingDesafio(prev => ({ ...prev, [desafioId]: true }));

    try {
      // Criar novo registro em user_desafio
      const { data, error } = await supabase
        .from('user_desafio')
        .insert({
          user_id: user.id,
          desafio_id: desafioId,
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar desafio:', error);
        
        // Se já existe (UNIQUE constraint), buscar o existente
        if (error.code === '23505' || error.message?.includes('duplicate')) {
          const { data: existing } = await supabase
            .from('user_desafio')
            .select('id')
            .eq('user_id', user.id)
            .eq('desafio_id', desafioId)
            .single();
          
          if (existing) {
            // Navegar para acompanhamento com o registro existente
            setSelectedUserDesafioId(existing.id);
            setCurrentView('acompanhamento');
            await loadDesafiosAtivos();
            setAddingDesafio(prev => ({ ...prev, [desafioId]: false }));
            return;
          }
        }
        
        alert(`Erro ao começar desafio: ${error.message}`);
        setAddingDesafio(prev => ({ ...prev, [desafioId]: false }));
        return;
      }

      if (data) {
        // Navegar para acompanhamento com o novo registro
        setSelectedUserDesafioId(data.id);
        setCurrentView('acompanhamento');
        await loadDesafiosAtivos();
      }
    } catch (error: any) {
      console.error('Erro ao começar desafio:', error);
      alert(`Erro inesperado: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setAddingDesafio(prev => ({ ...prev, [desafioId]: false }));
    }
  };

  // Ir para acompanhamento de um desafio já ativo
  const handleIrParaAcompanhamento = (desafioId: string) => {
    const userDesafio = isDesafioAtivo(desafioId);
    if (userDesafio) {
      setSelectedUserDesafioId(userDesafio.id);
      setCurrentView('acompanhamento');
    }
  };

  // Salvar progresso - adiciona ao histórico
  const handleSaveProgresso = async () => {
    if (!user || !selectedUserDesafioId || !currentUserDesafio) {
      return;
    }

    // Se o campo estiver vazio, não salvar
    if (!progressoTexto.trim()) {
      alert('Por favor, escreva algo antes de registrar.');
      return;
    }

    setSavingProgresso(true);

    try {
      const hoje = new Date().toISOString().split('T')[0];
      const desafioId = currentUserDesafio.desafio_id;
      
      // Verificar se já existe registro para hoje
      const { data: existing, error: checkError } = await supabase
        .from('desafio_historico')
        .select('id, anotacao')
        .eq('user_id', user.id)
        .eq('desafio_id', desafioId)
        .eq('data', hoje)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Erro ao verificar progresso existente:', checkError);
      }

      if (existing) {
        // Atualizar registro existente - usar anotacao (nome correto da coluna)
        const { error } = await supabase
          .from('desafio_historico')
          .update({ anotacao: progressoTexto.trim() })
          .eq('id', existing.id);
        
        if (error) {
          console.error('Erro ao atualizar progresso:', error);
          console.error('Detalhes:', {
            message: error.message,
            code: (error as any).code,
            details: (error as any).details,
            hint: (error as any).hint
          });
          alert('Erro ao salvar progresso: ' + error.message);
          setSavingProgresso(false);
          return;
        }
      } else {
        // Criar novo registro no histórico - usar anotacao (nome correto da coluna)
        const { error } = await supabase
          .from('desafio_historico')
          .insert({
            user_id: user.id,
            desafio_id: desafioId,
            data: hoje,
            anotacao: progressoTexto.trim(),
          });
        
        if (error) {
          console.error('Erro ao inserir progresso:', error);
          console.error('Detalhes:', {
            message: error.message,
            code: (error as any).code,
            details: (error as any).details,
            hint: (error as any).hint
          });
          alert('Erro ao salvar progresso: ' + error.message);
          setSavingProgresso(false);
          return;
        }
      }

      // Recarregar histórico para atualizar visualmente
      if (selectedUserDesafioId) {
        await loadHistorico(selectedUserDesafioId);
      }
      // Limpar campo após salvar
      setProgressoTexto('');
    } catch (error: any) {
      console.error('Erro ao salvar progresso:', error);
      alert('Erro inesperado ao salvar progresso: ' + (error?.message || 'Erro desconhecido'));
    } finally {
      setSavingProgresso(false);
    }
  };

  // Remover desafio
  const handleRemoveDesafio = async () => {
    if (!user || !selectedUserDesafioId) return;

    if (!confirm('Tem certeza que deseja remover este desafio?')) {
      return;
    }

    setRemoving(true);
    try {
      const { error } = await supabase
        .from('user_desafio')
        .delete()
        .eq('id', selectedUserDesafioId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Erro ao remover desafio:', error);
        alert(`Erro ao remover desafio: ${error.message}`);
        setRemoving(false);
      } else {
        // Limpar estado
        setSelectedUserDesafioId(null);
        setCurrentUserDesafio(null);
        setCurrentDesafio(null);
        setHistorico([]);
        setProgressoTexto('');
        // Redirecionar para lista
        setCurrentView('lista');
        // Recarregar desafios ativos
        await loadDesafiosAtivos();
      }
    } catch (error: any) {
      console.error('Erro ao remover desafio:', error);
      alert(`Erro inesperado: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setRemoving(false);
    }
  };

  // Marcar/desmarcar dia como concluído
  const handleToggleDiaConcluido = async (_diaNumero: number, diaData: string) => {
    if (!user || !selectedUserDesafioId || !currentUserDesafio) {
      return;
    }

    try {
      const desafioId = currentUserDesafio.desafio_id;
      
      // Verificar se já existe registro para este dia
      const { data: existing } = await supabase
        .from('desafio_historico')
        .select('id, anotacao')
        .eq('user_id', user.id)
        .eq('desafio_id', desafioId)
        .eq('data', diaData)
        .maybeSingle();

      if (existing) {
        // Se já existe, deletar (desmarcar)
        const { error } = await supabase
          .from('desafio_historico')
          .delete()
          .eq('id', existing.id);
        
        if (error) {
          console.error('Erro ao desmarcar dia:', error);
          console.error('Detalhes:', {
            message: error.message,
            code: (error as any).code,
            details: (error as any).details
          });
          alert('Erro ao desmarcar dia: ' + error.message);
          return;
        }
      } else {
        // Criar novo registro marcando como concluído - usar anotacao
        const { error } = await supabase
          .from('desafio_historico')
          .insert({
            user_id: user.id,
            desafio_id: desafioId,
            data: diaData,
            anotacao: '✅ Dia concluído',
          });
        
        if (error) {
          console.error('Erro ao marcar dia:', error);
          console.error('Detalhes:', {
            message: error.message,
            code: (error as any).code,
            details: (error as any).details,
            hint: (error as any).hint
          });
          alert('Erro ao marcar dia: ' + error.message);
          return;
        }
      }

      // Recarregar histórico para atualizar visualmente
      if (selectedUserDesafioId) {
        await loadHistorico(selectedUserDesafioId);
      }
    } catch (error) {
      console.error('Erro ao marcar dia como concluído:', error);
      alert('Erro inesperado ao marcar dia');
    }
  };

  // Calcular dias do desafio
  const getDiasDesafio = () => {
    if (!currentUserDesafio) return [];
    
    // Usar created_at como data de início do desafio
    const startDate = new Date(currentUserDesafio.created_at);
    startDate.setHours(0, 0, 0, 0);
    
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    const dias = [];
    for (let i = 0; i < 7; i++) {
      const data = new Date(startDate);
      data.setDate(startDate.getDate() + i);
      data.setHours(0, 0, 0, 0);
      
      const dataStr = data.toISOString().split('T')[0];
      const temProgresso = historico.some((h: DesafioHistorico) => h.data === dataStr);
      const isHoje = data.getTime() === hoje.getTime();
      const isPassado = data.getTime() < hoje.getTime();
      const isFuturo = data.getTime() > hoje.getTime();
      
      dias.push({
        data: dataStr,
        dia: i + 1,
        concluido: temProgresso,
        atual: isHoje,
        passado: isPassado,
        futuro: isFuturo,
      });
    }
    
    return dias;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BG_DARK }}>
        <Loader2 className="w-8 h-8 text-[#E53935] animate-spin" />
      </div>
    );
  }

  // Aba 2: Detalhes do Desafio
  if (currentView === 'detalhes' && currentDesafio) {
    const userDesafio = isDesafioAtivo(currentDesafio.id);
    const isAtivo = !!userDesafio;

    return (
      <div className="min-h-screen px-4 py-6 space-y-6" style={{ backgroundColor: BG_DARK }}>
        <header className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => setCurrentView('lista')} 
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white">{currentDesafio.titulo}</h1>
        </header>

        {/* Regra do Desafio - apenas tipografia */}
        <div className="space-y-2">
          <h3 className="text-white font-bold text-sm">Regra do Desafio</h3>
          <p className="text-[#A1A1A1] text-xs leading-relaxed italic">
            {currentDesafio.regra}
          </p>
        </div>

        {/* Objetivo do desafio */}
        <div className="rounded-2xl p-5 bg-[#141414] border border-[#E53935]/20 relative overflow-hidden">
          <div className="flex items-start gap-3 relative z-10">
            <div className="p-2 rounded-lg bg-[#E53935]/10 text-[#E53935]">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm mb-1">Objetivo</h3>
              <p className="text-[#A1A1A1] text-xs leading-relaxed">
                {currentDesafio.objetivo}
              </p>
            </div>
          </div>
        </div>

        {/* Criando Hábitos */}
        <div className="rounded-2xl p-5 bg-[#141414] border border-[#E53935]/20 relative overflow-hidden">
          <div className="flex items-start gap-3 relative z-10">
            <div className="p-2 rounded-lg bg-[#E53935]/10 text-[#E53935]">
              <Heart className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm mb-1">Criando Hábitos</h3>
              <p className="text-[#A1A1A1] text-xs leading-relaxed">
                Este desafio é uma ferramenta para criar hábitos. A consistência diária é o que transforma.
              </p>
            </div>
          </div>
        </div>

        {/* Botão Começar ou Ir para Acompanhamento */}
        {!isAtivo ? (
          <button
            onClick={() => handleComecarDesafio(currentDesafio.id)}
            disabled={addingDesafio[currentDesafio.id]}
            className="w-full py-3 rounded-xl bg-[#E53935] text-white font-bold text-sm hover:bg-[#C62828] transition-colors shadow-lg shadow-red-900/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {addingDesafio[currentDesafio.id] ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Iniciando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Começar desafio
              </>
            )}
          </button>
        ) : (
          <button
            onClick={() => handleIrParaAcompanhamento(currentDesafio.id)}
            className="w-full py-3 rounded-xl bg-[#E53935] text-white font-bold text-sm hover:bg-[#C62828] transition-colors shadow-lg shadow-red-900/20 flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            Ir para acompanhamento
          </button>
        )}
      </div>
    );
  }

  // Aba 3: Acompanhamento
  if (currentView === 'acompanhamento') {
    const dias = getDiasDesafio();

    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: BG_DARK }}>
        <div className="container mx-auto px-4 pt-6 max-w-md">
          <header className="mb-6 pt-4">
            <button 
              onClick={() => setCurrentView('lista')} 
              className="text-white flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-extrabold text-white mb-2" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontWeight: 800 }}>
              Desafio Ativo
            </h1>
            <p className="text-[#A1A1A1] text-sm leading-relaxed mb-1">
              Acompanhe sua evolução diária
            </p>
          </header>

          <div className="space-y-6">
            {/* Card do Desafio Ativo */}
            {currentUserDesafio && currentDesafio ? (
              <div className="rounded-2xl p-6 bg-[#141414] border border-[#E53935]/30 relative overflow-hidden">
                {(() => {
                  const Icon = getIcon(currentDesafio.titulo, currentDesafio.icon, currentDesafio.imagem_url);
                  return (
                    <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                      <Icon className="w-32 h-32 text-white" />
                    </div>
                  );
                })()}
                <div className="relative z-10">
                  <h2 className="text-white font-bold text-2xl mb-1">
                    {currentDesafio.titulo}
                  </h2>
                  <p className="text-[#A1A1A1] text-xs leading-relaxed">
                    {currentDesafio.regra}
                  </p>
                </div>
              </div>
            ) : (
              <div className="rounded-[18px] p-8 bg-[#141414] border text-center" style={{ borderColor: BORDER_COLOR }}>
                <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-4">
                  <Dumbbell className="w-6 h-6 text-[#A1A1A1]" />
                </div>
                <h3 className="text-white font-bold mb-2">Nenhum desafio selecionado.</h3>
                <button
                  onClick={() => setCurrentView('lista')}
                  className="mt-4 px-4 py-2 rounded-lg bg-[#E53935] text-white text-sm font-bold hover:bg-[#C62828] transition-colors"
                >
                  Escolher desafio
                </button>
              </div>
            )}

            {/* Progresso Semanal - Design igual à aba de Treinos */}
            {currentUserDesafio && currentDesafio && (
              <div className="rounded-2xl p-5 bg-[#141414] border border-white/5 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A1A1A1] text-xs font-medium uppercase tracking-wider mb-1">Progresso Semanal</p>
                    <h3 className="text-white text-xl font-bold flex items-baseline gap-1">
                      {dias.filter(d => d.concluido).length}/7 <span className="text-sm font-normal text-[#A1A1A1]">dias concluídos</span>
                    </h3>
                    <p className="text-[#A1A1A1] text-xs mt-1">
                      {Math.round((dias.filter(d => d.concluido).length / 7) * 100)}% da semana completa
                    </p>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  {dias.map((dia, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-2">
                      <button
                        onClick={() => handleToggleDiaConcluido(dia.dia, dia.data)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 ${
                          dia.concluido
                            ? 'bg-[#E53935] border-[#E53935] text-white'
                            : 'bg-transparent border-[#333] text-[#525252]'
                        }`}
                      >
                        {dia.concluido && <Check className="w-4 h-4" />}
                      </button>
                      <span className={`text-[10px] font-medium ${dia.atual ? 'text-white' : 'text-[#525252]'}`}>
                        Dia {dia.dia}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Campo de Progresso com Histórico */}
            {currentUserDesafio && currentDesafio && (
              <div className="rounded-[18px] p-5 bg-[#141414] border shadow-lg" style={{ borderColor: BORDER_COLOR }}>
                <h3 className="text-white font-bold text-sm mb-2">Progresso de Hoje</h3>
                <p className="text-[#A1A1A1] text-xs mb-4">
                  Registre suas mudanças: como está se sentindo, o que mudou no corpo, o que melhorou?
                </p>
                <textarea
                  value={progressoTexto}
                  onChange={(e) => setProgressoTexto(e.target.value)}
                  placeholder="Ex: Estou me sentindo mais disposto, percebi menos inchaço, minha energia melhorou..."
                  className="w-full rounded-xl bg-[#0D0D0D] border px-4 py-3 text-sm text-white placeholder:text-[#525252] focus:outline-none focus:border-[#E53935] transition-colors resize-none mb-4"
                  style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
                  rows={5}
                />
                
                {/* Botão Registrar */}
                <button
                  onClick={handleSaveProgresso}
                  disabled={savingProgresso || !progressoTexto.trim()}
                  className="w-full py-3 rounded-lg bg-[#E53935] text-white font-bold text-sm hover:bg-[#C62828] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
                >
                  {savingProgresso ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Registrando...
                    </>
                  ) : (
                    'Registrar Progresso'
                  )}
                </button>
                
                {/* Histórico de Mudanças */}
                {historico.filter((h) => {
                  const texto = h.anotacao || h.progresso || '';
                  const isConcluido = texto.trim() === '✅ Dia concluído' || texto.trim().startsWith('✅');
                  return !isConcluido && texto.trim() !== '';
                }).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <h4 className="text-white font-bold text-xs mb-3">Seus Registros</h4>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {historico
                        .filter((h) => {
                          const texto = h.anotacao || h.progresso || '';
                          const isConcluido = texto.trim() === '✅ Dia concluído' || texto.trim().startsWith('✅');
                          return !isConcluido && texto.trim() !== '';
                        })
                        .map((entry) => {
                          const hoje = new Date().toISOString().split('T')[0];
                          const isHoje = entry.data === hoje;
                          const texto = entry.anotacao || entry.progresso || '';
                          
                          return (
                            <div
                              key={entry.id}
                              className={`rounded-xl p-3 border ${
                                isHoje 
                                  ? 'bg-[#E53935]/5 border-[#E53935]/20' 
                                  : 'bg-[#0D0D0D] border-white/5'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[#E53935] text-[10px] font-bold">
                                  {new Date(entry.data).toLocaleDateString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })}
                                </span>
                                {isHoje && (
                                  <span className="text-[#E53935] text-[10px] font-bold bg-[#E53935]/10 px-2 py-0.5 rounded-full">
                                    HOJE
                                  </span>
                                )}
                              </div>
                              <p className="text-white text-xs leading-relaxed whitespace-pre-wrap">
                                {texto}
                              </p>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }

  // Aba 1: Lista de Desafios
  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: BG_DARK }}>
      <div className="container mx-auto px-4 pt-6 max-w-md">
        <header className="mb-6 pt-4">
          <h1 className="text-2xl font-extrabold text-white mb-2" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontWeight: 800 }}>
            Desafios
          </h1>
          <p className="text-[#A1A1A1] text-sm leading-relaxed">
            Desafios semanais para consolidar disciplina, foco e alta performance.
          </p>
        </header>

        <div className="space-y-4">
          {desafiosDisponiveis.length === 0 ? (
            <div className="bg-[#141414] rounded-2xl p-8 border text-center" style={{ borderColor: BORDER_COLOR }}>
              <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-4">
                <Dumbbell className="w-6 h-6 text-[#A1A1A1]" />
              </div>
              <h3 className="text-white font-bold mb-2">Nenhum desafio disponível</h3>
              <p className="text-sm text-[#A1A1A1] mb-4">
                Os desafios ainda não foram carregados.
              </p>
            </div>
          ) : (
            desafiosDisponiveis.map((desafio) => {
              const Icon = getIcon(desafio.titulo, desafio.icon, desafio.imagem_url);
              const isAtivo = isDesafioAtivo(desafio.id) !== null;

              return (
                <button
                  key={desafio.id}
                  onClick={() => {
                    setSelectedDesafioId(desafio.id);
                    setCurrentView('detalhes');
                  }}
                  className="w-full p-4 rounded-xl border transition-all duration-200 shadow-lg hover:border-[#E53935]/50 text-left"
                  style={{
                    backgroundColor: CARD_BG,
                    borderColor: isAtivo ? 'rgba(229, 57, 53, 0.5)' : BORDER_COLOR,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                      <Icon className="w-6 h-6 text-[#E53935]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <h2 className="text-white font-bold text-sm pt-0.5">
                          {desafio.titulo}
                        </h2>
                        {isAtivo && (
                          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#E53935]/20 text-[#E53935] border border-[#E53935]/30 whitespace-nowrap flex-shrink-0">
                            Em andamento
                          </span>
                        )}
                      </div>
                      <p className="text-[#A1A1A1] text-xs leading-relaxed line-clamp-2">
                        <span className="font-medium text-white">Regra:</span> {desafio.regra}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
